import { QueryClient } from '@tanstack/react-query';

type HttpError = { response?: { status?: number } };

export const getHttpStatus = (error: unknown): number | undefined => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const status = (error as HttpError).response?.status;
    return typeof status === 'number' ? status : undefined;
  }
  return undefined;
};

const getRetryDelay = (attemptIndex: number): number =>
  Math.min(1000 * Math.pow(2, attemptIndex), 4000);

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: (failureCount, error: unknown) => {
        const status = getHttpStatus(error);
        if (status !== undefined) {
          // Don't retry auth errors
          if (status === 401 || status === 403) return false;

          // Don't retry client errors except timeout/rate-limit
          if (status >= 400 && status < 500 && status !== 408 && status !== 429) return false;
        }

        // Retry network/server errors up to 3 times
        return failureCount < 3;
      },
      retryDelay: getRetryDelay,
      refetchOnWindowFocus: false,
      // Enable refetch on reconnect for better offline support
      refetchOnReconnect: 'always',
      // Network mode: try to fetch from cache if offline
      networkMode: 'offlineFirst',
    },
    mutations: {
      retry: (failureCount, error: unknown) => {
        const status = getHttpStatus(error);
        if (status !== undefined) {
          if (status >= 500 || status === 429) return failureCount < 2;
          return false;
        }
        return failureCount < 2;
      },
      retryDelay: getRetryDelay,
      // Mutations are online-only by default
      networkMode: 'online',
    },
  },
});
